// src/pages/message-center/components/ShiftModal.jsx
import React, { useState, useEffect } from 'react';
import {
  X,
  Clock,
  UserRound,
  DollarSign,
  Check,
  Loader,
  AlertCircle,
  Plus,
  Trash2,
} from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import { createShift, updateShift } from '../../../services/api/messageCenterShifts';
import DatePicker from '../../../components/ui/DatePicker/DatePicker';
import Dropdown from '../../../components/ui/Dropdown/Dropdown';
import TimePicker from '../../../components/ui/TimePicker/TimePicker';
import { blockInvalidNumberKeys, sanitizeNumber } from '../utils/rangeValidation';
import styles from './ShiftModal.module.css';

const EMPTY_FORM = {
  shiftDate: new Date().toISOString().split('T')[0],
  shiftTypeId: '',
  shiftLeaderId: '',
  targetSalesValue: '',
  excludeAllFromCommission: false,
  excludeLeaderFromCommission: false,
  staff: [],
};

const ShiftModal = ({ mode, shift, shiftTypes, businessUsers, onClose, onSuccess }) => {
  const { t } = useLocalization();
  const { success, error: showError, warning } = useAlert();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [timesResetNotice, setTimesResetNotice] = useState(false);

  // Populate form when editing
  useEffect(() => {
    if (mode === 'edit' && shift) {
      const shiftLeaderId = shift.shiftLeaderId ? String(shift.shiftLeaderId) : '';
      const staff = (shift.staff || []).map((s) => ({
        userId: String(s.userId),
        startTime: s.startTime,
        endTime: s.endTime,
      }));

      if (shiftLeaderId && !staff.some((s) => s.userId === shiftLeaderId)) {
        const defaults = shift.shiftTypeId ? getShiftTypeDefaults(String(shift.shiftTypeId)) : null;
        staff.push({
          userId: shiftLeaderId,
          startTime: defaults?.startTime || '',
          endTime: defaults?.endTime || '',
        });
      }

      setFormData({
        shiftDate: shift.shiftDate || EMPTY_FORM.shiftDate,
        shiftTypeId: shift.shiftTypeId ? String(shift.shiftTypeId) : '',
        shiftLeaderId,
        targetSalesValue: shift.targetSalesValue != null ? String(shift.targetSalesValue) : '',
        excludeAllFromCommission: !!shift.excludeAllFromCommission,
        excludeLeaderFromCommission: !!shift.excludeLeaderFromCommission,
        staff,
      });
    } else {
      setFormData(EMPTY_FORM);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, shift]);

  const getShiftTypeDefaults = (shiftTypeId) => {
    const st = shiftTypes.find((t2) => String(t2.id) === String(shiftTypeId));
    return st ? { startTime: st.defaultStartTime, endTime: st.defaultEndTime, target: st.defaultTarget } : null;
  };

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };

      // When shift type changes, reset all existing staff rows' times to the new defaults
      if (field === 'shiftTypeId') {
        const defaults = getShiftTypeDefaults(value);
        if (defaults && prev.staff.length > 0) {
          next.staff = prev.staff.map((s) => ({ ...s, startTime: defaults.startTime, endTime: defaults.endTime }));
          setTimesResetNotice(true);
        }
        next.targetSalesValue = defaults?.target != null ? String(defaults.target) : '';
      }

      if (field === 'shiftLeaderId') {
        const previousLeaderId = prev.shiftLeaderId;
        const existingLeaderRow = value ? prev.staff.find((s) => s.userId === value) : null;
        const withoutOldAndNewLeader = prev.staff.filter((s) => {
          if (previousLeaderId && s.userId === previousLeaderId) return false;
          if (value && s.userId === value) return false;
          return true;
        });

        if (value) {
          const defaults = getShiftTypeDefaults(prev.shiftTypeId);
          next.staff = [
            ...withoutOldAndNewLeader,
            existingLeaderRow || {
              userId: value,
              startTime: defaults?.startTime || '',
              endTime: defaults?.endTime || '',
            },
          ];
        } else {
          next.staff = withoutOldAndNewLeader;
        }

        if (previousLeaderId && previousLeaderId !== value) {
          warning(t('messageCenterShifts.leaderStaffRowUpdatedNotice'));
        }
      }

      return next;
    });
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleAddStaff = () => {
    const defaults = getShiftTypeDefaults(formData.shiftTypeId);
    setFormData((prev) => ({
      ...prev,
      staff: [
        ...prev.staff,
        {
          userId: '',
          startTime: defaults?.startTime || '',
          endTime: defaults?.endTime || '',
        },
      ],
    }));
  };

  const handleStaffChange = (index, field, value) => {
    setFormData((prev) => {
      const staff = [...prev.staff];
      staff[index] = { ...staff[index], [field]: value };
      return { ...prev, staff };
    });
    const errorKey = `staff.${index}.${field}`;
    if (errors[errorKey]) {
      setErrors((prev) => ({ ...prev, [errorKey]: '' }));
    }
  };

  const handleRemoveStaff = (index) => {
    setFormData((prev) => ({ ...prev, staff: prev.staff.filter((_, i) => i !== index) }));
  };

  const getAvailableUsersForRow = (index) => {
    const selectedElsewhere = formData.staff
      .filter((_, i) => i !== index)
      .map((s) => s.userId);
    return businessUsers.filter((u) => !selectedElsewhere.includes(String(u.id)));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.shiftDate) {
      newErrors.shiftDate = t('messageCenterShifts.validation.shiftDateRequired');
    }
    if (!formData.shiftTypeId) {
      newErrors.shiftTypeId = t('messageCenterShifts.validation.shiftTypeRequired');
    }
    if (!formData.shiftLeaderId) {
      newErrors.shiftLeaderId = t('messageCenterShifts.validation.shiftLeaderRequired');
    }

    if (!formData.targetSalesValue) {
      newErrors.targetSalesValue = t('messageCenterShifts.validation.targetSalesValueRequired');
    } else if (isNaN(formData.targetSalesValue) || parseFloat(formData.targetSalesValue) < 0) {
      newErrors.targetSalesValue = t('messageCenterShifts.validation.targetSalesValueInvalid');
    }

    formData.staff.forEach((s, index) => {
      if (!s.userId) {
        newErrors[`staff.${index}.userId`] = t('messageCenterShifts.validation.staffUserRequired');
      }
      if (!s.startTime || !s.endTime) {
        newErrors[`staff.${index}.time`] = t('messageCenterShifts.validation.staffTimeRequired');
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      showError(t('messageCenterShifts.validation.fixErrors'));
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        shiftDate: formData.shiftDate,
        shiftTypeId: parseInt(formData.shiftTypeId, 10),
        shiftLeaderId: parseInt(formData.shiftLeaderId, 10),
        targetSalesValue: formData.targetSalesValue ? parseFloat(formData.targetSalesValue) : null,
        excludeAllFromCommission: formData.excludeAllFromCommission,
        excludeLeaderFromCommission: formData.excludeLeaderFromCommission,
        staff: formData.staff.map((s) => ({
          userId: parseInt(s.userId, 10),
          startTime: s.startTime,
          endTime: s.endTime,
        })),
      };

      if (mode === 'edit') {
        await updateShift(shift.id, payload);
        success(t('messageCenterShifts.shiftUpdatedSuccess'));
      } else {
        await createShift(payload);
        success(t('messageCenterShifts.shiftCreatedSuccess'));
      }

      onSuccess();
    } catch (err) {
      console.error('Error saving shift:', err);
      showError(
        err.response?.data?.message ||
          t('messageCenterShifts.failedToSaveShift')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={(e) => { if (e.target === e.currentTarget && !isSubmitting) onClose(); }}>
      <div className={styles.modalContent}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <h2>{mode === 'edit' ? t('messageCenterShifts.editShift') : t('messageCenterShifts.createShift')}</h2>
          <button onClick={onClose} className={styles.closeButton} disabled={isSubmitting}>
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className={styles.modalBody}>
          <div className={styles.formGrid}>
            {/* Shift Date */}
            <div className={styles.formGroup}>
              <label htmlFor="shiftDate">{t('messageCenterShifts.shiftDate')} *</label>
              <DatePicker
                value={formData.shiftDate}
                onChange={(d) => handleChange('shiftDate', d)}
                disabled={isSubmitting}
              />
              {errors.shiftDate && (
                <span className={styles.errorText}><AlertCircle size={12} />{errors.shiftDate}</span>
              )}
            </div>

            {/* Shift Type */}
            <div className={styles.formGroup}>
              <label htmlFor="shiftTypeId">{t('messageCenterShifts.shiftType')} *</label>
              <Dropdown
                value={formData.shiftTypeId}
                onChange={(v) => handleChange('shiftTypeId', v != null ? String(v) : '')}
                options={shiftTypes}
                displayKey="name"
                valueKey="id"
                placeholder={t('messageCenterShifts.selectShiftType')}
                icon={<Clock size={18} />}
                allowClear={false}
                disabled={isSubmitting}
                className={errors.shiftTypeId ? styles.error : ''}
              />
              {errors.shiftTypeId && (
                <span className={styles.errorText}><AlertCircle size={12} />{errors.shiftTypeId}</span>
              )}
            </div>

            {/* Shift Leader */}
            <div className={styles.formGroup}>
              <label htmlFor="shiftLeaderId">{t('messageCenterShifts.shiftLeader')} *</label>
              <Dropdown
                value={formData.shiftLeaderId}
                onChange={(v) => handleChange('shiftLeaderId', v != null ? String(v) : '')}
                options={businessUsers}
                displayKey="name"
                valueKey="id"
                placeholder={t('messageCenterShifts.selectLeader')}
                icon={<UserRound size={18} />}
                allowClear={false}
                disabled={isSubmitting}
                className={errors.shiftLeaderId ? styles.error : ''}
                searchable
              />
              {errors.shiftLeaderId && (
                <span className={styles.errorText}><AlertCircle size={12} />{errors.shiftLeaderId}</span>
              )}
            </div>

            {/* Target Sales Value */}
            <div className={styles.formGroup}>
              <label htmlFor="targetSalesValue">{t('messageCenterShifts.targetSalesValue')} *</label>
              <div className={styles.inputWrapper}>
                <DollarSign className={styles.inputIcon} size={18} />
                <input
                  type="number"
                  id="targetSalesValue"
                  value={formData.targetSalesValue}
                  placeholder={t('messageCenterShifts.targetSalesValuePlaceholder')}
                  onKeyDown={blockInvalidNumberKeys}
                  onChange={(e) => handleChange('targetSalesValue', sanitizeNumber(e.target.value))}
                  min="0"
                  step="0.01"
                  className={errors.targetSalesValue ? styles.error : ''}
                  disabled={isSubmitting}
                />
              </div>
              {errors.targetSalesValue && (
                <span className={styles.errorText}><AlertCircle size={12} />{errors.targetSalesValue}</span>
              )}
            </div>

            {/* Commission flags (full width) */}
            <div className={`${styles.formGroup} ${styles.fullWidth}`}>
              <label>{t('messageCenterShifts.commissionFlags')}</label>
              <div className={styles.checkboxRow}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={formData.excludeAllFromCommission}
                    onChange={(e) => handleChange('excludeAllFromCommission', e.target.checked)}
                    disabled={isSubmitting}
                  />
                  {t('messageCenterShifts.excludeAllFromCommission')}
                </label>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={formData.excludeLeaderFromCommission}
                    onChange={(e) => handleChange('excludeLeaderFromCommission', e.target.checked)}
                    disabled={isSubmitting}
                  />
                  {t('messageCenterShifts.excludeLeaderFromCommission')}
                </label>
              </div>
            </div>

            {/* Shift Staff (full width) */}
            <div className={`${styles.formGroup} ${styles.fullWidth}`}>
              <div className={styles.staffHeader}>
                <label>{t('messageCenterShifts.shiftStaff')}</label>
                <button
                  type="button"
                  onClick={handleAddStaff}
                  className={styles.addStaffButton}
                  disabled={isSubmitting}
                >
                  <Plus size={16} />
                  {t('messageCenterShifts.addStaff')}
                </button>
              </div>

              {timesResetNotice && formData.staff.length > 0 && (
                <p className={styles.timesResetNote}>{t('messageCenterShifts.timesResetNote')}</p>
              )}

              {formData.staff.length > 0 && (
                <div className={styles.staffList}>
                  <div className={styles.staffListHeader}>
                    <span>{t('messageCenterShifts.staffMember')}</span>
                    <span>{t('messageCenterShifts.startTime')}</span>
                    <span>{t('messageCenterShifts.endTime')}</span>
                    <span />
                  </div>
                  {formData.staff.map((s, index) => {
                    const availableUsers = getAvailableUsersForRow(index);
                    const isLeaderRow = !!formData.shiftLeaderId && s.userId === formData.shiftLeaderId;
                    return (
                      <div className={styles.staffRow} key={index}>
                        <Dropdown
                          value={s.userId}
                          onChange={(v) => handleStaffChange(index, 'userId', v != null ? String(v) : '')}
                          options={availableUsers.map((u) => ({
                            ...u,
                            name: isLeaderRow && String(u.id) === s.userId
                              ? `${u.name} (${t('messageCenterShifts.shiftLeader')})`
                              : u.name,
                          }))}
                          displayKey="name"
                          valueKey="id"
                          placeholder={t('messageCenterShifts.selectUser')}
                          allowClear={false}
                          size="compact"
                          disabled={isSubmitting || isLeaderRow}
                          className={errors[`staff.${index}.userId`] ? styles.error : ''}
                          searchable
                        />
                        <TimePicker
                          value={s.startTime}
                          onChange={(v) => handleStaffChange(index, 'startTime', v)}
                          placeholder={t('messageCenterShifts.selectTime')}
                          size="compact"
                          error={!!errors[`staff.${index}.time`] && !s.startTime}
                          disabled={isSubmitting}
                        />
                        <TimePicker
                          value={s.endTime}
                          onChange={(v) => handleStaffChange(index, 'endTime', v)}
                          placeholder={t('messageCenterShifts.selectTime')}
                          size="compact"
                          error={!!errors[`staff.${index}.time`] && !s.endTime}
                          disabled={isSubmitting}
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveStaff(index)}
                          className={styles.removeStaffButton}
                          disabled={isSubmitting || isLeaderRow}
                          title={isLeaderRow ? t('messageCenterShifts.leaderRowLocked') : t('messageCenterShifts.removeStaff')}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className={styles.modalFooter}>
          <button type="button" onClick={onClose} className={styles.cancelButton} disabled={isSubmitting}>
            {t('common.cancel')}
          </button>
          <button type="submit" onClick={handleSubmit} className={styles.submitButton} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader className={styles.buttonSpinner} size={18} />
                {mode === 'edit' ? t('common.updating') : t('common.creating')}
              </>
            ) : (
              <>
                <Check size={18} />
                {mode === 'edit' ? t('messageCenterShifts.saveShift') : t('messageCenterShifts.createShift')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShiftModal;
